import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  MenuItem, Select, TextField, Switch,
} from '@mui/material';
import { C } from '../theme';

const API = process.env.REACT_APP_API_URL || 'https://api.binko.me';

function venueHeaders() {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('binko_venue_token')}`,
  };
}

function timeSince(dateStr) {
  const mins = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  return `${hrs}h ${mins % 60}m ago`;
}

function formatTime(dateStr) {
  return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// ─── Winners Tab ────────────────────────────────────────────────────────────

function WinnersTab() {
  const [winners, setWinners]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [redeeming, setRedeeming]   = useState({});
  const [confirmCode, setConfirmCode] = useState(null); // code string to confirm
  const [pinInput, setPinInput]       = useState('');
  const [pinError, setPinError]       = useState('');

  const fetchWinners = useCallback(async () => {
    try {
      const res  = await fetch(`${API}/api/venue/winner-codes`, { headers: venueHeaders() });
      const data = await res.json();
      if (data.status === 'success') setWinners(data.winners || []);
      else setError('Failed to load winners');
    } catch (e) {
      setError('Failed to load winners');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWinners();
    const t = setInterval(fetchWinners, 60000);
    return () => clearInterval(t);
  }, [fetchWinners]);

  function openConfirm(code) {
    setConfirmCode(code);
    setPinInput('');
    setPinError('');
  }

  function closeConfirm() {
    setConfirmCode(null);
    setPinInput('');
    setPinError('');
  }

  async function handleRedeem() {
    const code = confirmCode;
    if (pinInput.trim() !== code) {
      setPinError('Incorrect code. Ask the player to show their win code.');
      return;
    }
    setRedeeming(r => ({ ...r, [code]: true }));
    setPinError('');
    try {
      const res  = await fetch(`${API}/api/venue/redeem/${code}`, {
        method: 'POST', headers: venueHeaders(),
      });
      const data = await res.json();
      if (data.status === 'success') {
        setWinners(w => w.map(x => x.code === code ? { ...x, status: 'redeemed' } : x));
        closeConfirm();
      } else {
        setPinError(data.error || 'Redeem failed');
      }
    } catch (e) {
      setPinError('Redeem failed');
    } finally {
      setRedeeming(r => ({ ...r, [code]: false }));
    }
  }

  const statusColor = s => s === 'redeemed' ? C.teal : s === 'expired' ? C.textDisabled : C.yellow;
  const statusLabel = s => s === 'redeemed' ? 'Redeemed' : s === 'expired' ? 'Expired' : 'Pending';

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: '60px' }}>
      <CircularProgress size={28} sx={{ color: C.teal }} />
    </Box>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '16px' }}>
        <Typography sx={{ fontSize: '13px', color: C.textMuted }}>
          Today's winner codes
        </Typography>
        <Button onClick={fetchWinners} sx={{ fontSize: '12px', color: C.teal, textTransform: 'none', minWidth: 0 }}>
          Refresh
        </Button>
      </Box>

      {error && (
        <Typography sx={{ fontSize: '13px', color: C.orange, mb: '12px' }}>{error}</Typography>
      )}

      {winners.length === 0 && (
        <Box sx={{
          background: C.bgCard, borderRadius: '12px', border: `0.5px solid ${C.border}`,
          py: '48px', textAlign: 'center',
        }}>
          <Typography sx={{ fontSize: '24px', mb: '8px' }}>🏆</Typography>
          <Typography sx={{ fontSize: '14px', color: C.textMuted }}>No winners yet today</Typography>
          <Typography sx={{ fontSize: '12px', color: C.textDisabled, mt: '4px' }}>
            Winner codes appear here when players qualify
          </Typography>
        </Box>
      )}

      {winners.map(w => (
        <Box key={w.id} sx={{
          background: C.bgCard, borderRadius: '12px', border: `0.5px solid ${C.border}`,
          px: '16px', py: '14px', mb: '10px',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: '10px' }}>
            <Box>
              <Typography sx={{ fontSize: '15px', fontWeight: 700, color: C.textPrimary }}>
                {w.alias}
              </Typography>
              <Typography sx={{ fontSize: '12px', color: C.textMuted, mt: '2px' }}>
                {w.game_type === 'trivia' ? '\u{1F9E0} Trivia' : '\u{1F3C6} Tournament'} &middot; {formatTime(w.won_at)}
              </Typography>
              {w.offer_text && (
                <Typography sx={{ fontSize: '12px', color: C.teal, mt: '3px' }}>
                  {w.offer_text}
                </Typography>
              )}
            </Box>
            <Box sx={{
              background: `${statusColor(w.status)}18`,
              border: `0.5px solid ${statusColor(w.status)}55`,
              borderRadius: '99px', px: '10px', py: '3px',
            }}>
              <Typography sx={{ fontSize: '11px', fontWeight: 700, color: statusColor(w.status) }}>
                {statusLabel(w.status)}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{
              background: 'rgba(255,255,255,0.04)', borderRadius: '8px',
              border: `0.5px solid ${C.border}`, px: '16px', py: '8px',
            }}>
              <Typography sx={{ fontSize: '11px', color: C.textMuted, mb: '2px', letterSpacing: '0.5px' }}>
                WIN CODE
              </Typography>
              <Typography sx={{ fontSize: '22px', fontWeight: 900, color: w.status === 'redeemed' ? C.textDisabled : C.yellow, letterSpacing: '6px' }}>
                {w.status === 'redeemed' ? w.code : '••••'}
              </Typography>
              {w.status !== 'redeemed' && (
                <Typography sx={{ fontSize: '10px', color: C.textDisabled, mt: '2px' }}>
                  Ask player to show their code
                </Typography>
              )}
            </Box>

            {w.status === 'pending' && (
              <Button
                onClick={() => openConfirm(w.code)}
                sx={{
                  background: C.teal, color: '#fff', textTransform: 'none',
                  borderRadius: '10px', px: '20px', py: '10px', fontWeight: 700, fontSize: '14px',
                  '&:hover': { background: '#0b8a91' },
                }}
              >
                Redeem
              </Button>
            )}
          </Box>
        </Box>
      ))}

      {/* PIN confirm dialog */}
      <Dialog open={!!confirmCode} onClose={closeConfirm}
        PaperProps={{ sx: { background: C.bgCard, border: `0.5px solid ${C.border}`, borderRadius: '16px', mx: '20px', width: '100%' } }}>
        <DialogTitle sx={{ color: C.textPrimary, fontSize: '16px', fontWeight: 700 }}>
          Enter win code to redeem
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '14px', color: C.textMuted, mb: '16px' }}>
            Ask the player to show their win code. Type it below to confirm redemption.
          </Typography>
          <TextField
            fullWidth
            autoFocus
            placeholder="4-digit code"
            value={pinInput}
            onChange={e => { setPinInput(e.target.value.slice(0, 4)); setPinError(''); }}
            onKeyDown={e => { if (e.key === 'Enter') handleRedeem(); }}
            inputProps={{ maxLength: 4, style: { textAlign: 'center', fontSize: '28px', fontWeight: 900, letterSpacing: '10px', color: '#FBBC15' } }}
            sx={{
              '& .MuiOutlinedInput-root': {
                background: 'rgba(255,255,255,0.04)', borderRadius: '10px',
                '& fieldset': { borderColor: pinError ? '#FE7210' : 'rgba(255,255,255,0.12)' },
                '&.Mui-focused fieldset': { borderColor: pinError ? '#FE7210' : '#0D9DA5' },
              },
            }}
          />
          {pinError && (
            <Typography sx={{ fontSize: '12px', color: '#FE7210', mt: '8px' }}>{pinError}</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: '20px', pb: '16px', gap: '8px' }}>
          <Button onClick={closeConfirm} sx={{ color: '#9CA3AF', textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleRedeem}
            disabled={pinInput.length !== 4 || !!redeeming[confirmCode]}
            sx={{ background: '#0D9DA5', color: '#fff', textTransform: 'none', borderRadius: '8px', px: '16px',
              '&:disabled': { background: 'rgba(13,157,165,0.3)', color: 'rgba(255,255,255,0.4)' } }}
          >
            {redeeming[confirmCode] ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Redeem'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Offers Tab ─────────────────────────────────────────────────────────────

function OffersTab() {
  const [offers, setOffers]   = useState({ trivia: null, tournament: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState({});
  const [saved, setSaved]     = useState({});
  const [error, setError]     = useState('');

  // Local edit state
  const [triviaText, setTriviaText]           = useState('');
  const [triviaThreshold, setTriviaThreshold] = useState('100');
  const [triviaActive, setTriviaActive]       = useState(false);
  const [tournText, setTournText]             = useState('');
  const [tournActive, setTournActive]         = useState(false);

  useEffect(() => {
    async function fetchOffers() {
      try {
        const res  = await fetch(`${API}/api/venue/my-offers`, { headers: venueHeaders() });
        const data = await res.json();
        if (data.status === 'success') {
          const t = data.offers.find(o => o.offer_type === 'trivia');
          const r = data.offers.find(o => o.offer_type === 'tournament');
          setOffers({ trivia: t || null, tournament: r || null });
          if (t) {
            setTriviaText(t.offer_text || '');
            setTriviaThreshold(String(t.score_threshold || 100));
            setTriviaActive(!!t.is_active);
          }
          if (r) {
            setTournText(r.offer_text || '');
            setTournActive(!!r.is_active);
          }
        }
      } catch (e) {
        setError('Failed to load offers');
      } finally {
        setLoading(false);
      }
    }
    fetchOffers();
  }, []);

  async function saveOffer(offerType) {
    setSaving(s => ({ ...s, [offerType]: true }));
    setSaved(s => ({ ...s, [offerType]: false }));
    setError('');
    try {
      const body = offerType === 'trivia'
        ? { offer_text: triviaText, score_threshold: parseInt(triviaThreshold, 10), is_active: triviaActive }
        : { offer_text: tournText, is_active: tournActive };
      const res  = await fetch(`${API}/api/venue/offers/${offerType}`, {
        method: 'PATCH', headers: venueHeaders(), body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.status === 'success') {
        setSaved(s => ({ ...s, [offerType]: true }));
        setTimeout(() => setSaved(s => ({ ...s, [offerType]: false })), 2000);
      } else {
        setError(data.error || 'Save failed');
      }
    } catch (e) {
      setError('Save failed');
    } finally {
      setSaving(s => ({ ...s, [offerType]: false }));
    }
  }

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: '60px' }}>
      <CircularProgress size={28} sx={{ color: C.teal }} />
    </Box>
  );

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      background: 'rgba(255,255,255,0.04)', borderRadius: '10px',
      color: C.textPrimary, fontSize: '14px',
      '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
      '&.Mui-focused fieldset': { borderColor: C.teal },
    },
    '& input::placeholder, & textarea::placeholder': { color: C.textMuted, opacity: 1 },
  };

  return (
    <Box>
      {error && (
        <Typography sx={{ fontSize: '13px', color: C.orange, mb: '16px' }}>{error}</Typography>
      )}

      <Typography sx={{ fontSize: '11px', color: C.textDisabled, mb: '16px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
        Offers appear on the venue screen and in the player app
      </Typography>

      {/* Trivia offer card */}
      <Box sx={{
        background: C.bgCard, borderRadius: '12px', border: `0.5px solid ${C.border}`,
        px: '16px', py: '16px', mb: '12px',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '14px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography sx={{ fontSize: '18px' }}>{'\u{1F9E0}'}</Typography>
            <Box>
              <Typography sx={{ fontSize: '14px', fontWeight: 700, color: C.textPrimary }}>Trivia Offer</Typography>
              <Typography sx={{ fontSize: '11px', color: C.textMuted }}>Awarded when a player hits your score target</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Typography sx={{ fontSize: '11px', color: triviaActive ? C.teal : C.textDisabled }}>
              {triviaActive ? 'On' : 'Off'}
            </Typography>
            <Switch
              checked={triviaActive}
              onChange={e => setTriviaActive(e.target.checked)}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: C.teal },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: C.teal },
              }}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: '10px', mb: '10px' }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '11px', color: C.textMuted, mb: '6px' }}>Score target (pts)</Typography>
            <TextField
              fullWidth
              type="number"
              value={triviaThreshold}
              onChange={e => setTriviaThreshold(e.target.value)}
              inputProps={{ min: 10, max: 500 }}
              placeholder="100"
              sx={inputSx}
            />
          </Box>
        </Box>

        <Box sx={{ mb: '12px' }}>
          <Typography sx={{ fontSize: '11px', color: C.textMuted, mb: '6px' }}>Offer text (shown to players)</Typography>
          <TextField
            fullWidth multiline rows={2}
            value={triviaText}
            onChange={e => setTriviaText(e.target.value.slice(0, 200))}
            placeholder="e.g. Free drink on your next order"
            sx={inputSx}
          />
          <Typography sx={{ fontSize: '11px', color: C.textDisabled, mt: '4px', textAlign: 'right' }}>
            {triviaText.length}/200
          </Typography>
        </Box>

        <Button
          fullWidth
          onClick={() => saveOffer('trivia')}
          disabled={!!saving.trivia}
          sx={{
            background: saved.trivia ? C.teal : 'rgba(13,157,165,0.15)',
            color: saved.trivia ? '#fff' : C.teal,
            border: `0.5px solid ${C.teal}55`,
            borderRadius: '10px', textTransform: 'none', fontWeight: 700, py: '10px',
            '&:hover': { background: 'rgba(13,157,165,0.25)' },
            '&:disabled': { opacity: 0.5 },
          }}
        >
          {saving.trivia ? <CircularProgress size={16} sx={{ color: C.teal }} /> : saved.trivia ? 'Saved \u2713' : 'Save trivia offer'}
        </Button>
      </Box>

      {/* Tournament offer card */}
      <Box sx={{
        background: C.bgCard, borderRadius: '12px', border: `0.5px solid ${C.border}`,
        px: '16px', py: '16px', mb: '12px',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '14px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Typography sx={{ fontSize: '18px' }}>{'\u{1F3C6}'}</Typography>
            <Box>
              <Typography sx={{ fontSize: '14px', fontWeight: 700, color: C.textPrimary }}>Tournament Offer</Typography>
              <Typography sx={{ fontSize: '11px', color: C.textMuted }}>Awarded to the tournament winner</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Typography sx={{ fontSize: '11px', color: tournActive ? C.yellow : C.textDisabled }}>
              {tournActive ? 'On' : 'Off'}
            </Typography>
            <Switch
              checked={tournActive}
              onChange={e => setTournActive(e.target.checked)}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: C.yellow },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: C.yellow },
              }}
            />
          </Box>
        </Box>

        <Box sx={{ mb: '12px' }}>
          <Typography sx={{ fontSize: '11px', color: C.textMuted, mb: '6px' }}>Offer text (shown to players)</Typography>
          <TextField
            fullWidth multiline rows={2}
            value={tournText}
            onChange={e => setTournText(e.target.value.slice(0, 200))}
            placeholder="e.g. Free shot for the tournament winner"
            sx={inputSx}
          />
          <Typography sx={{ fontSize: '11px', color: C.textDisabled, mt: '4px', textAlign: 'right' }}>
            {tournText.length}/200
          </Typography>
        </Box>

        <Button
          fullWidth
          onClick={() => saveOffer('tournament')}
          disabled={!!saving.tournament}
          sx={{
            background: saved.tournament ? C.yellow : 'rgba(251,188,21,0.1)',
            color: saved.tournament ? '#000' : C.yellow,
            border: `0.5px solid ${C.yellow}55`,
            borderRadius: '10px', textTransform: 'none', fontWeight: 700, py: '10px',
            '&:hover': { background: 'rgba(251,188,21,0.2)' },
            '&:disabled': { opacity: 0.5 },
          }}
        >
          {saving.tournament ? <CircularProgress size={16} sx={{ color: C.yellow }} /> : saved.tournament ? 'Saved \u2713' : 'Save tournament offer'}
        </Button>
      </Box>

      <Box sx={{
        background: 'rgba(255,255,255,0.02)', borderRadius: '10px',
        border: `0.5px solid ${C.border}`, px: '14px', py: '12px',
      }}>
        <Typography sx={{ fontSize: '12px', color: C.textDisabled, lineHeight: 1.6 }}>
          {'\u2139\uFE0F'} Offers appear in the carousel on the venue spectator screen and in the player app. Toggle off to hide an offer without deleting it.
        </Typography>
      </Box>
    </Box>
  );
}

// ─── Main Dashboard ──────────────────────────────────────────────────────────

export default function VenueDashboard() {
  const venueName = localStorage.getItem('binko_venue_name') || 'Venue';

  const [activeTab, setActiveTab] = useState(0); // 0=Tonight 1=Winners 2=Offers

  const [checkins, setCheckins]       = useState([]);
  const [total, setTotal]             = useState(0);
  const [male, setMale]               = useState(0);
  const [female, setFemale]           = useState(0);
  const [loading, setLoading]         = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError]             = useState('');

  const [checkinMode, setCheckinMode]   = useState('auto');
  const [modeLoading, setModeLoading]   = useState(false);

  const [pending, setPending]           = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [actionLoading, setActionLoading]   = useState({});

  const [kickTarget, setKickTarget]   = useState(null);
  const [kickLoading, setKickLoading] = useState(false);
  const [kickReason, setKickReason]   = useState('');
  const [kickNotes, setKickNotes]     = useState('');

  const [reportTarget, setReportTarget] = useState(null);
  const [reason, setReason]             = useState('');
  const [notes, setNotes]               = useState('');
  const [reportLoading, setReportLoading] = useState(false);
  const [reportDone, setReportDone]     = useState(false);

  const fetchMode = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/venue/me`, { headers: venueHeaders() });
      if (res.ok) {
        const data = await res.json();
        setCheckinMode(data.venue.checkin_mode || 'auto');
      }
    } catch (err) {}
  }, []);

  const fetchPending = useCallback(async () => {
    try {
      setPendingLoading(true);
      const res = await fetch(`${API}/api/venue/pending`, { headers: venueHeaders() });
      if (res.ok) {
        const data = await res.json();
        setPending(data.pending || []);
      }
    } catch (err) {}
    finally { setPendingLoading(false); }
  }, []);

  const fetchCheckins = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/venue/checkins`, { headers: venueHeaders() });
      if (res.status === 401) {
        localStorage.removeItem('binko_venue_token');
        window.location.href = '/venue/login';
        return;
      }
      const data = await res.json();
      setCheckins(data.checkins || []);
      setTotal(data.total || 0);
      setMale(data.male || 0);
      setFemale(data.female || 0);
      setLastUpdated(new Date());
      setError('');
    } catch (err) {
      setError('Failed to load check-ins');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMode();
    fetchCheckins();
    const interval = setInterval(fetchCheckins, 30000);
    return () => clearInterval(interval);
  }, [fetchCheckins, fetchMode]);

  useEffect(() => {
    if (checkinMode !== 'manual') { setPending([]); return; }
    fetchPending();
    const interval = setInterval(fetchPending, 10000);
    return () => clearInterval(interval);
  }, [checkinMode, fetchPending]);

  function closeKick() {
    setKickTarget(null); setKickReason(''); setKickNotes('');
  }

  async function handleKick() {
    if (!kickTarget || !kickReason) return;
    setKickLoading(true);
    try {
      const res = await fetch(`${API}/api/venue/checkins/${kickTarget.id}/kick`, {
        method: 'PATCH', headers: venueHeaders(),
        body: JSON.stringify({ reason: kickReason, notes: kickNotes }),
      });
      if (res.ok) { closeKick(); fetchCheckins(); }
    } catch (err) {}
    finally { setKickLoading(false); }
  }

  async function handleReport() {
    if (!reportTarget || !reason) return;
    setReportLoading(true);
    try {
      const res = await fetch(`${API}/api/venue/reports`, {
        method: 'POST', headers: venueHeaders(),
        body: JSON.stringify({ checkin_id: reportTarget.checkinId, reported_user_id: reportTarget.userId, reason, notes }),
      });
      if (res.ok) setReportDone(true);
    } catch (err) {}
    finally { setReportLoading(false); }
  }

  function closeReport() {
    setReportTarget(null); setReason(''); setNotes(''); setReportDone(false);
  }

  async function handleMode(mode) {
    setModeLoading(true);
    try {
      const res = await fetch(`${API}/api/venue/mode`, {
        method: 'PATCH', headers: venueHeaders(),
        body: JSON.stringify({ mode }),
      });
      if (res.ok) setCheckinMode(mode);
    } catch (err) {}
    finally { setModeLoading(false); }
  }

  async function handleApprove(checkinId) {
    setActionLoading(a => ({ ...a, [checkinId]: true }));
    try {
      await fetch(`${API}/api/venue/checkins/${checkinId}/approve`, {
        method: 'POST', headers: venueHeaders(),
      });
      setPending(p => p.filter(r => r.id !== checkinId));
      fetchCheckins();
    } catch (err) {}
    finally { setActionLoading(a => ({ ...a, [checkinId]: false })); }
  }

  async function handleDeny(checkinId) {
    setActionLoading(a => ({ ...a, [checkinId]: true }));
    try {
      await fetch(`${API}/api/venue/checkins/${checkinId}/deny`, {
        method: 'POST', headers: venueHeaders(),
      });
      setPending(p => p.filter(r => r.id !== checkinId));
    } catch (err) {}
    finally { setActionLoading(a => ({ ...a, [checkinId]: false })); }
  }

  function handleLogout() {
    localStorage.removeItem('binko_venue_token');
    localStorage.removeItem('binko_venue_name');
    window.location.href = '/venue/login';
  }

  const tabs = ['Tonight', 'Winners', 'Offers'];

  return (
    <Box sx={{ minHeight: '100vh', background: C.bgApp, display: 'flex', flexDirection: 'column' }}>

      {/* Top bar */}
      <Box sx={{
        background: C.bgCard, borderBottom: `0.5px solid ${C.border}`,
        px: '20px', py: '14px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 100,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <img src="/logo.png" alt="Binko" style={{ height: '28px', width: 'auto' }} />
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: C.textPrimary }}>
            {venueName}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Button onClick={fetchCheckins} sx={{ color: C.teal, fontSize: '12px', textTransform: 'none', minWidth: 0 }}>
            Refresh
          </Button>
          <Button onClick={() => { window.history.pushState({}, '', '/venue/settings'); window.dispatchEvent(new PopStateEvent('popstate')); }}
            sx={{ color: C.textMuted, fontSize: '12px', textTransform: 'none', minWidth: 0 }}>
            Settings
          </Button>
          <Button onClick={() => window.open('/venue/spectator', '_blank')}
            sx={{ color: C.teal, fontSize: '12px', textTransform: 'none', minWidth: 0,
              border: `0.5px solid ${C.teal}55`, borderRadius: '6px', px: '10px' }}>
            Spectator \u2197
          </Button>
          <Button onClick={handleLogout} sx={{ color: C.textMuted, fontSize: '12px', textTransform: 'none', minWidth: 0 }}>
            Sign out
          </Button>
        </Box>
      </Box>

      <Box sx={{ px: '20px', py: '20px', maxWidth: '720px', width: '100%', mx: 'auto' }}>

        {/* Stats row — always visible */}
        <Box sx={{ display: 'flex', gap: '12px', mb: '20px' }}>
          {[
            { label: 'Total here', value: total },
            { label: 'Male', value: male },
            { label: 'Female', value: female },
          ].map(s => (
            <Box key={s.label} sx={{
              flex: 1, background: C.bgCard, borderRadius: '12px',
              border: `0.5px solid ${C.border}`, px: '16px', py: '14px', textAlign: 'center',
            }}>
              <Typography sx={{ fontSize: '24px', fontWeight: 700, color: C.textPrimary }}>{s.value}</Typography>
              <Typography sx={{ fontSize: '12px', color: C.textMuted, mt: '2px' }}>{s.label}</Typography>
            </Box>
          ))}
        </Box>

        {/* Tab bar */}
        <Box sx={{
          display: 'flex', gap: '4px', mb: '20px',
          background: 'rgba(255,255,255,0.04)', borderRadius: '10px', p: '4px',
        }}>
          {tabs.map((tab, i) => (
            <Button key={tab} onClick={() => setActiveTab(i)} sx={{
              flex: 1, fontSize: '13px', textTransform: 'none', borderRadius: '8px',
              py: '7px', minWidth: 0,
              background: activeTab === i ? C.bgElevated : 'transparent',
              color: activeTab === i ? C.textPrimary : C.textMuted,
              fontWeight: activeTab === i ? 700 : 400,
              border: activeTab === i ? `0.5px solid ${C.border}` : 'none',
              '&:hover': { background: activeTab === i ? C.bgElevated : 'rgba(255,255,255,0.04)' },
            }}>
              {tab}
            </Button>
          ))}
        </Box>

        {/* Tonight tab */}
        {activeTab === 0 && (
          <Box>
            {lastUpdated && (
              <Typography sx={{ fontSize: '11px', color: C.textMuted, mb: '12px' }}>
                Last updated: {lastUpdated.toLocaleTimeString()} \u2014 auto-refreshes every 30s
              </Typography>
            )}

            {/* Mode toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '20px' }}>
              <Typography sx={{ fontSize: '13px', color: C.textMuted, mr: '4px' }}>Check-in mode:</Typography>
              {['auto', 'manual'].map(m => (
                <Button key={m} disabled={modeLoading} onClick={() => handleMode(m)} sx={{
                  fontSize: '13px', textTransform: 'none', borderRadius: '8px',
                  px: '16px', py: '6px', minWidth: 0,
                  background: checkinMode === m ? C.yellow : 'transparent',
                  color: checkinMode === m ? '#000' : C.textMuted,
                  border: `0.5px solid ${checkinMode === m ? C.yellow : C.border}`,
                  fontWeight: checkinMode === m ? 700 : 400,
                  '&:hover': { background: checkinMode === m ? C.yellow : 'rgba(255,255,255,0.05)' },
                  '&:disabled': { opacity: 0.5 },
                }}>
                  {m.charAt(0).toUpperCase() + m.slice(1)}
                </Button>
              ))}
            </Box>

            {/* Pending requests */}
            {checkinMode === 'manual' && (
              <Box sx={{ mb: '24px' }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: C.teal, mb: '10px' }}>
                  Pending requests
                </Typography>
                {pendingLoading && pending.length === 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: '20px' }}>
                    <CircularProgress size={20} sx={{ color: C.teal }} />
                  </Box>
                )}
                {!pendingLoading && pending.length === 0 && (
                  <Box sx={{ background: C.bgCard, borderRadius: '12px', border: `0.5px solid ${C.border}`, py: '20px', textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '13px', color: C.textMuted }}>No pending requests</Typography>
                  </Box>
                )}
                {pending.map(r => (
                  <Box key={r.id} sx={{
                    background: C.bgCard, borderRadius: '12px', border: `0.5px solid ${C.border}`,
                    px: '16px', py: '12px', mb: '8px',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Typography sx={{ fontSize: '16px' }}>{r.gender === 'female' ? '\u2640' : '\u2642'}</Typography>
                      <Box>
                        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: C.textPrimary }}>{r.alias}</Typography>
                        <Typography sx={{ fontSize: '11px', color: C.textMuted }}>{timeSince(r.created_at)}</Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: '8px' }}>
                      <Button size="small" disabled={!!actionLoading[r.id]} onClick={() => handleDeny(r.id)}
                        sx={{ fontSize: '12px', textTransform: 'none', color: C.textMuted, border: `0.5px solid ${C.border}`, borderRadius: '8px', px: '10px',
                          '&:hover': { borderColor: C.orange, color: C.orange }, '&:disabled': { opacity: 0.4 } }}>
                        Deny
                      </Button>
                      <Button size="small" disabled={!!actionLoading[r.id]} onClick={() => handleApprove(r.id)}
                        sx={{ fontSize: '12px', textTransform: 'none', color: C.teal, border: `0.5px solid ${C.teal}`, borderRadius: '8px', px: '10px',
                          '&:hover': { background: 'rgba(13,157,165,0.1)' }, '&:disabled': { opacity: 0.4 } }}>
                        {actionLoading[r.id] ? <CircularProgress size={12} sx={{ color: C.teal }} /> : 'Approve'}
                      </Button>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {error && <Typography sx={{ fontSize: '13px', color: C.orange, mb: '12px' }}>{error}</Typography>}

            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: '40px' }}>
                <CircularProgress size={28} sx={{ color: C.teal }} />
              </Box>
            )}

            {!loading && checkins.length === 0 && (
              <Box sx={{ background: C.bgCard, borderRadius: '12px', border: `0.5px solid ${C.border}`, py: '40px', textAlign: 'center' }}>
                <Typography sx={{ fontSize: '14px', color: C.textMuted }}>No one checked in right now</Typography>
              </Box>
            )}

            {!loading && checkins.map(c => (
              <Box key={c.id} sx={{
                background: C.bgCard, borderRadius: '12px', border: `0.5px solid ${C.border}`,
                px: '16px', py: '14px', mb: '10px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Typography sx={{ fontSize: '18px' }}>
                    {c.gender === 'female' ? '\u2640' : '\u2642'}
                  </Typography>
                  <Box>
                    <Typography sx={{ fontSize: '15px', fontWeight: 600, color: C.textPrimary }}>{c.alias}</Typography>
                    <Typography sx={{ fontSize: '12px', color: C.textMuted }}>{timeSince(c.created_at)}</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: '8px' }}>
                  <Button size="small"
                    onClick={() => setReportTarget({ checkinId: c.id, userId: c.user_id, alias: c.alias })}
                    sx={{ fontSize: '12px', textTransform: 'none', color: C.textMuted, border: `0.5px solid ${C.border}`, borderRadius: '8px', px: '10px',
                      '&:hover': { borderColor: C.orange, color: C.orange } }}>
                    Report
                  </Button>
                  <Button size="small"
                    onClick={() => setKickTarget({ id: c.id, alias: c.alias })}
                    sx={{ fontSize: '12px', textTransform: 'none', color: C.orange, border: `0.5px solid ${C.orange}`, borderRadius: '8px', px: '10px',
                      '&:hover': { background: 'rgba(254,114,16,0.1)' } }}>
                    Kick
                  </Button>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* Winners tab */}
        {activeTab === 1 && <WinnersTab />}

        {/* Offers tab */}
        {activeTab === 2 && <OffersTab />}

      </Box>

      {/* Kick dialog */}
      <Dialog open={!!kickTarget} onClose={closeKick}
        PaperProps={{ sx: { background: C.bgCard, border: `0.5px solid ${C.border}`, borderRadius: '16px', mx: '20px', width: '100%' } }}>
        <DialogTitle sx={{ color: C.textPrimary, fontSize: '16px', fontWeight: 700 }}>
          Remove {kickTarget?.alias}?
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', pt: '4px' }}>
            <Typography sx={{ fontSize: '14px', color: C.textMuted }}>
              Their session will end. Select a reason \u2014 this will be logged as a report.
            </Typography>
            <Select value={kickReason} onChange={e => setKickReason(e.target.value)} displayEmpty
              sx={{ color: kickReason ? C.textPrimary : C.textMuted, fontSize: '14px',
                background: 'rgba(255,255,255,0.04)', borderRadius: '10px',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.12)' } }}>
              <MenuItem value="" disabled><em style={{ color: C.textMuted }}>Select reason</em></MenuItem>
              <MenuItem value="not_at_venue">Not actually at the venue</MenuItem>
              <MenuItem value="inappropriate_alias">Profane or inappropriate alias</MenuItem>
              <MenuItem value="inappropriate_behaviour">Inappropriate behaviour toward guests</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
            <TextField multiline rows={2}
              placeholder="Additional notes (optional, max 200 chars)"
              value={kickNotes} onChange={e => setKickNotes(e.target.value.slice(0, 200))}
              sx={{ '& .MuiOutlinedInput-root': {
                background: 'rgba(255,255,255,0.04)', borderRadius: '10px', color: C.textPrimary, fontSize: '14px',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                '&.Mui-focused fieldset': { borderColor: C.orange },
              }, '& textarea::placeholder': { color: C.textMuted, opacity: 1 } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: '20px', pb: '16px', gap: '8px' }}>
          <Button onClick={closeKick} sx={{ color: C.textMuted, textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleKick} disabled={!kickReason || kickLoading}
            sx={{ background: C.orange, color: '#fff', textTransform: 'none', borderRadius: '8px', px: '16px',
              '&:hover': { background: '#e5650e' },
              '&:disabled': { background: 'rgba(254,114,16,0.3)', color: 'rgba(255,255,255,0.4)' } }}>
            {kickLoading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Remove & report'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Report dialog */}
      <Dialog open={!!reportTarget} onClose={closeReport}
        PaperProps={{ sx: { background: C.bgCard, border: `0.5px solid ${C.border}`, borderRadius: '16px', mx: '20px', width: '100%' } }}>
        <DialogTitle sx={{ color: C.textPrimary, fontSize: '16px', fontWeight: 700 }}>
          {reportDone ? 'Report submitted' : `Report ${reportTarget?.alias}`}
        </DialogTitle>
        <DialogContent>
          {reportDone ? (
            <Typography sx={{ fontSize: '14px', color: C.textMuted }}>
              Thank you. Our team will review this report.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', pt: '4px' }}>
              <Select value={reason} onChange={e => setReason(e.target.value)} displayEmpty
                sx={{ color: reason ? C.textPrimary : C.textMuted, fontSize: '14px',
                  background: 'rgba(255,255,255,0.04)', borderRadius: '10px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.12)' } }}>
                <MenuItem value="" disabled><em style={{ color: C.textMuted }}>Select reason</em></MenuItem>
                <MenuItem value="not_at_venue">Not actually at the venue</MenuItem>
                <MenuItem value="inappropriate_alias">Profane or inappropriate alias</MenuItem>
                <MenuItem value="inappropriate_behaviour">Inappropriate behaviour toward guests</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
              <TextField multiline rows={3}
                placeholder="Additional notes (optional, max 200 chars)"
                value={notes} onChange={e => setNotes(e.target.value.slice(0, 200))}
                sx={{ '& .MuiOutlinedInput-root': {
                  background: 'rgba(255,255,255,0.04)', borderRadius: '10px', color: C.textPrimary, fontSize: '14px',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                  '&.Mui-focused fieldset': { borderColor: C.teal },
                }, '& textarea::placeholder': { color: C.textMuted, opacity: 1 } }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: '20px', pb: '16px', gap: '8px' }}>
          {reportDone ? (
            <Button onClick={closeReport} sx={{ background: C.teal, color: '#fff', textTransform: 'none', borderRadius: '8px', px: '16px' }}>
              Done
            </Button>
          ) : (
            <>
              <Button onClick={closeReport} sx={{ color: C.textMuted, textTransform: 'none' }}>Cancel</Button>
              <Button onClick={handleReport} disabled={!reason || reportLoading}
                sx={{ background: C.teal, color: '#fff', textTransform: 'none', borderRadius: '8px', px: '16px',
                  '&:disabled': { background: 'rgba(13,157,165,0.3)', color: 'rgba(255,255,255,0.4)' } }}>
                {reportLoading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Submit report'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
